import { useState, useEffect } from 'react';
import type { Ward } from '../App';
import './Dashboard.css';

interface DashboardProps {
  councilId: string;
  apiBase: string;
  onWardSelect: (wardId: string) => void;
}

function Dashboard({ councilId, apiBase, onWardSelect }: DashboardProps) {
  const [wards, setWards] = useState<Ward[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'sweet_spot' | 'name' | 'green_vote'>('sweet_spot');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchWards();
  }, [councilId, sortBy]);

  const fetchWards = async () => {
    setLoading(true);
    try {
      const url = `${apiBase}/councils/${councilId}/wards?sort=${sortBy}&order=desc`;
      const res = await fetch(url);
      const data = await res.json();
      setWards(data);
    } catch (err) {
      console.error('Failed to fetch wards:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredWards = filterStatus === 'all'
    ? wards
    : wards.filter((w) => w.status === filterStatus);

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'priority': return 'status-priority';
      case 'hold': return 'status-hold';
      case 'low_roi': return 'status-low';
      case 'defensive': return 'status-defensive';
      default: return '';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'priority': return 'Priority';
      case 'hold': return 'Hold';
      case 'low_roi': return 'Low ROI';
      case 'defensive': return 'Defensive';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading wards...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Controls */}
      <div className="dashboard-controls">
        <div className="control-group">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
            <option value="sweet_spot">Sweet-spot Score</option>
            <option value="green_vote">Green Vote %</option>
            <option value="name">Ward Name</option>
          </select>
        </div>

        <div className="control-group">
          <label>Filter:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Wards</option>
            <option value="priority">Priority Only</option>
            <option value="hold">Hold Only</option>
            <option value="low_roi">Low ROI Only</option>
            <option value="defensive">Defensive Only</option>
          </select>
        </div>

        <div className="control-stats">
          <span className="stat-badge">
            <strong>{filteredWards.length}</strong> wards
          </span>
          <span className="stat-badge">
            <strong>{filteredWards.filter(w => w.status === 'priority').length}</strong> priority
          </span>
        </div>
      </div>

      {/* Map Placeholder */}
      <div className="ward-map-container">
        <div className="ward-map-placeholder">
          <svg viewBox="0 0 400 300" className="ward-map-svg">
            {/* Simple geometric representation */}
            <rect x="50" y="50" width="100" height="100" fill="#15803d" opacity="0.7" />
            <rect x="150" y="50" width="100" height="100" fill="#86efac" opacity="0.7" />
            <rect x="50" y="150" width="100" height="100" fill="#94a3b8" opacity="0.7" />
            <rect x="150" y="150" width="100" height="100" fill="#cbd5e1" opacity="0.7" />
            <text x="200" y="180" fontSize="12" fill="#003366" textAnchor="middle">
              Ward Map
            </text>
            <text x="200" y="200" fontSize="10" fill="#64748b" textAnchor="middle">
              (Interactive map requires GeoJSON boundaries)
            </text>
          </svg>
          <div className="map-legend">
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#15803d' }}></div>
              <span>High Priority (0.8+)</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#86efac' }}></div>
              <span>Medium (0.5-0.8)</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#cbd5e1' }}></div>
              <span>Low (&lt;0.5)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Ward Table */}
      <div className="ward-table-container">
        <table className="ward-table">
          <thead>
            <tr>
              <th>Ward</th>
              <th>Current %</th>
              <th>Target %</th>
              <th>Sweet-spot</th>
              <th>FRAI</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredWards.map((ward) => {
              const greenVote = (ward.green_vote_2024 ?? 0) * 100;
              const voteTarget = (ward.vote_target ?? 0) * 100;
              const sweetSpot = ward.sweet_spot_score ?? 0;
              const greenAgreement = ward.green_agreement_current ?? 0;
              
              // Parse councillors from current_councillors string
              const councillors = ward.current_councillors 
                ? ward.current_councillors.split('|').map((c: string) => {
                    const [party, name] = c.split(':');
                    return { party, name };
                  })
                : [];
              const greenCouncillors = councillors.filter((c: { party: string; name: string }) => c.party === 'GRN').length;
              
              return (
                <tr key={ward.id} onClick={() => onWardSelect(ward.id)}>
                  <td className="ward-name">
                    {ward.name}
                    {greenCouncillors > 0 && (
                      <span className="green-badge" title={`${greenCouncillors} Green councillor(s)`}>
                        🟢 {greenCouncillors}
                      </span>
                    )}
                  </td>
                  <td>{greenVote > 0 ? greenVote.toFixed(1) + '%' : '—'}</td>
                  <td>{voteTarget > 0 ? voteTarget.toFixed(1) + '%' : '—'}</td>
                  <td>
                    <div className="score-bar">
                      <div
                        className="score-fill"
                        style={{ width: `${sweetSpot * 100}%` }}
                      ></div>
                      <span className="score-value">{sweetSpot > 0 ? sweetSpot.toFixed(2) : '—'}</span>
                    </div>
                  </td>
                  <td>{greenAgreement > 0 ? greenAgreement.toFixed(3) : '—'}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(ward.status ?? '')}`}>
                      {getStatusLabel(ward.status ?? '') || '—'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn-view"
                      onClick={(e) => {
                        e.stopPropagation();
                        onWardSelect(ward.id);
                      }}
                    >
                      View →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredWards.length === 0 && (
          <div className="no-results">
            <p>No wards match the current filter.</p>
          </div>
        )}
      </div>

      {/* Insights */}
      <div className="dashboard-insights">
        <div className="insight-card">
          <h3>💡 Top Recommendation</h3>
          {filteredWards.length > 0 && (
            <p>
              Focus on <strong>{filteredWards[0].name}</strong> with sweet-spot score{' '}
              <strong>{((filteredWards[0].sweet_spot_score ?? 0)).toFixed(2)}</strong>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
